import { TabType } from '@/container/ScribeTabs/index.v2.types';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

export { TabType };

export type Tab = {
  name: TabType;
  documentId?: string;
};

/**
 * Canonical tab identity: same semantic tab (name) and same document, if any.
 * Shared by window reconciliation and the focus-intent handshake so their
 * equality checks can never drift out of lockstep.
 */
export const tabsEqual = (a: Tab | undefined, b: Tab | undefined): boolean =>
  a?.name === b?.name && a?.documentId === b?.documentId;

export type SessionWindow = {
  tabs: Tab[];
  activeTab?: Tab;
};

export type SessionWindowsState = {
  sessionWindows: SessionWindow[];
  activeWindowIndex: number;
};

const defaultSessionWindowsState: SessionWindowsState = {
  sessionWindows: [],
  activeWindowIndex: 0,
};

/**
 * Atom family for session windows state, keyed by sessionId.
 * Each session has its own independent windows state.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const sessionWindowsAtomFamily = atomFamily((_sessionId: string) =>
  atom<SessionWindowsState>(defaultSessionWindowsState)
);

/**
 * Collapses a two-window split back to a single window: the right window's
 * tabs append to the left's and its active tab stays active. Any other
 * cardinality is returned unchanged (same reference), so writing this
 * through a jotai setter is a no-op outside an open split.
 */
export const mergedSessionWindowsState = (
  data: SessionWindowsState
): SessionWindowsState => {
  if (data.sessionWindows.length !== 2) return data;
  const leftWindow = data.sessionWindows.at(0);
  const rightWindow = data.sessionWindows.at(1);
  return {
    ...data,
    sessionWindows: [
      {
        tabs: [...(leftWindow?.tabs ?? []), ...(rightWindow?.tabs ?? [])],
        activeTab: rightWindow?.activeTab,
      },
    ],
    activeWindowIndex: 0,
  };
};

/**
 * Per-session "focus this tab" intent, keyed by sessionId. Set by the create
 * paths (document / dictation / GPCCMP / letter) to the tab the user just
 * created; window reconciliation honours it as the active tab until the tab has
 * landed and settled in the desired list, then it is cleared. This is what makes
 * "create a tab, take me to it" robust against the query-lag / reconcile race
 * that a single one-shot `setActiveTab` loses.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const pendingTabFocusAtomFamily = atomFamily((_sessionId: string) =>
  atom<Tab | null>(null)
);
